import React, { useState, useEffect, useMemo } from 'react';
import {
  FiUsers,
  FiBookOpen,
  FiCalendar,
  FiBarChart2,
  FiDollarSign,
  FiTag,
  FiPackage,
  FiShoppingBag,
  FiSearch,
  FiTrash2,
  FiUpload,
  FiUserPlus,
  FiPlus,
  FiCheckCircle,
  FiArrowUpRight,
  FiBriefcase,
  FiUser,
  FiBell,
} from 'react-icons/fi';
import apiService from '../../../services/api';
import logger from '../../../utils/logging/logger';
import { useAuth } from '../../../context/AuthContext';

/* =========================================================
   Refined-editorial admin dashboard
   - Hero with personalized greeting and date
   - Compact KPI strip
   - Inline quick actions
   - Management grid (with live counts where available)
   ========================================================= */

import { Page, Hero, HeroDivider, HeroEyebrow, HeroTitle, HeroGreetingAr, HeroGreetingName, HeroSub, KpiGrid, Kpi, KpiLabel, KpiSwatch, KpiValue, KpiCaption } from './AdminDashboard.styles';
import { SectionHead, SectionTitle, SectionHint, TodoStack, TodoBanner, BannerIcon, BannerBody, BannerTitle, BannerCaption, BannerArrow, QuickRow, QuickChip, QuickIcon } from './adminDashboard/TodoSection.styles';
import { ManageGrid, ManageCard, ManageHeader, ManageIcon, ManageStat, ManageTitle, ManageDesc, ManageFooter } from './adminDashboard/ManageCards.styles';
import { ToolList, ToolItem, ToolIcon, ToolLabel, ToolName, ToolHint } from './adminDashboard/ToolList.styles';
/* ---------- Brand-cohesive accent palette ---------- */
/* Earthy, desaturated tones that complement the warm gold primary without
   competing with it. Each section item picks one slot. */
const ACCENT = {
  gold:       { tint: 'rgba(184, 149, 106, 0.12)', solid: '#B8956A' },
  indigo:     { tint: 'rgba(91, 107, 161, 0.12)',  solid: '#5B6BA1' },
  sage:       { tint: 'rgba(107, 142, 127, 0.14)', solid: '#5C8676' },
  terracotta: { tint: 'rgba(178, 125, 92, 0.14)',  solid: '#A86A4A' },
  teal:       { tint: 'rgba(79, 130, 144, 0.12)',  solid: '#3E7A82' },
  plum:       { tint: 'rgba(126, 95, 126, 0.14)',  solid: '#7B5E7D' },
  amber:      { tint: 'rgba(168, 146, 84, 0.14)',  solid: '#9A8344' },
}

/* ============================================================== */

const ARABIC_GREETING = 'السلام عليكم';

const formatDate = () =>
  new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

const formatNumber = (n) => new Intl.NumberFormat().format(n ?? 0);

function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: null,
    totalCourses: null,
    totalStudents: null,
    totalInstructors: null,
  });
  const [pendingExtensions, setPendingExtensions] = useState(0);
  const [pendingCorrections, setPendingCorrections] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [usersRes, coursesRes, studentsRes, instructorsRes, extRes, corrRes] = await Promise.allSettled([
          apiService.getUsers(),
          apiService.getCourses(),
          apiService.getStudents(),
          apiService.getInstructors(),
          apiService.getPendingExtensionCount(),
          apiService.getAttendanceCorrectionRequests('pending'),
        ]);

        if (cancelled) return;
        if (extRes.status === 'fulfilled' && extRes.value?.success) {
          setPendingExtensions(extRes.value.count || 0);
        }
        if (corrRes.status === 'fulfilled' && corrRes.value?.success) {
          setPendingCorrections(Array.isArray(corrRes.value.requests) ? corrRes.value.requests.length : 0);
        }

        const safe = (r, key) => (r.status === 'fulfilled' && r.value?.success ? r.value[key]?.length ?? 0 : 0);

        setStats({
          totalUsers: safe(usersRes, 'users'),
          totalCourses: safe(coursesRes, 'courses'),
          totalStudents: safe(studentsRes, 'students'),
          totalInstructors: safe(instructorsRes, 'instructors'),
        });
      } catch (error) {
        logger.error('Error fetching dashboard data', { error });
      }
    };

    load();
    if (window.innerWidth <= 768) window.scrollTo(0, 0);

    return () => { cancelled = true; };
  }, []);

  const firstName = useMemo(() => {
    const raw = user?.firstName || user?.first_name || user?.name || user?.email?.split('@')[0] || '';
    return String(raw).split(' ')[0];
  }, [user]);

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.32, delay, ease: [0.22, 1, 0.36, 1] },
  });

  const kpis = [
    { label: 'Total users',    value: stats.totalUsers,       caption: 'All accounts',      icon: <FiUsers size={12} />,     accent: ACCENT.indigo },
    { label: 'Active courses', value: stats.totalCourses,     caption: 'Currently offered', icon: <FiBookOpen size={12} />,  accent: ACCENT.gold },
    { label: 'Students',       value: stats.totalStudents,    caption: 'Enrolled learners', icon: <FiUser size={12} />,      accent: ACCENT.sage },
    { label: 'Instructors',    value: stats.totalInstructors, caption: 'Teaching staff',    icon: <FiBriefcase size={12} />, accent: ACCENT.teal },
  ];

  const quickActions = [
    { to: '/admin/users',           label: 'Add user',         icon: <FiUserPlus size={15} /> },
    { to: '/admin/courses',         label: 'New course',       icon: <FiPlus size={15} /> },
    { to: '/admin/attendance',      label: 'Take attendance',  icon: <FiCheckCircle size={15} /> },
    { to: '/admin/promo-codes',     label: 'Promo code',       icon: <FiTag size={15} /> },
  ];

  const manageCards = [
    {
      to: '/admin/users',
      icon: <FiUsers size={18} />,
      title: 'People',
      desc: 'Students, instructors, and admin accounts.',
      stat: stats.totalUsers != null ? `${formatNumber(stats.totalUsers)} total` : '—',
      accent: ACCENT.indigo,
    },
    {
      to: '/admin/courses',
      icon: <FiBookOpen size={18} />,
      title: 'Courses',
      desc: 'Create, edit, and publish course content.',
      stat: stats.totalCourses != null ? `${formatNumber(stats.totalCourses)} active` : '—',
      accent: ACCENT.gold,
    },
    {
      to: '/admin/electives',
      icon: <FiBookOpen size={18} />,
      title: 'Electives',
      desc: 'Manage elective offerings and assignments.',
      stat: '',
      accent: ACCENT.terracotta,
    },
    {
      to: '/admin/attendance',
      icon: <FiCalendar size={18} />,
      title: 'Attendance',
      desc: 'Track sessions and student attendance.',
      stat: '',
      accent: ACCENT.sage,
    },
    {
      to: '/admin/reports',
      icon: <FiBarChart2 size={18} />,
      title: 'Reports',
      desc: 'Enrollment, performance, and revenue insights.',
      stat: '',
      accent: ACCENT.teal,
    },
    {
      to: '/admin/pricing',
      icon: <FiDollarSign size={18} />,
      title: 'Pricing',
      desc: 'Course pricing and payment configuration.',
      stat: '',
      accent: ACCENT.amber,
    },
  ];

  const tools = [
    { to: '/admin/attendance-corrections', icon: <FiCheckCircle size={15} />, name: 'Correction requests', hint: 'Approve or deny attendance disputes' },
    { to: '/admin/promo-codes',         icon: <FiTag size={15} />,         name: 'Promo codes',        hint: 'Discount and referral codes' },
    { to: '/admin/bulk-pricing',        icon: <FiPackage size={15} />,     name: 'Bulk pricing',       hint: 'Multi-course bundles' },
    { to: '/admin/additional-products', icon: <FiShoppingBag size={15} />, name: 'Additional products',hint: 'Sponsorships, donations' },
    { to: '/admin/purchase-lookup',     icon: <FiSearch size={15} />,      name: 'Purchase lookup',    hint: 'Find an order or receipt' },
    { to: '/admin/import-export',       icon: <FiUpload size={15} />,      name: 'Import / export',    hint: 'Bulk data operations' },
    { to: '/admin/deleted-items',       icon: <FiTrash2 size={15} />,      name: 'Deleted items',      hint: 'Restore or purge' },
    { to: '/admin/permissions',         icon: <FiUsers size={15} />,       name: 'Permissions',        hint: 'Grant content access' },
  ];

  return (
    <Page>
      <Hero>
        <HeroEyebrow>{formatDate()}</HeroEyebrow>
        <HeroTitle>
          <HeroGreetingAr lang="ar" aria-label="Assalamu Alaikum">
            {ARABIC_GREETING}
          </HeroGreetingAr>
          {firstName && <HeroGreetingName>{firstName}.</HeroGreetingName>}
        </HeroTitle>
        <HeroSub>
          Here's a snapshot of the Faith Insight Portal and quick access to everything you manage.
        </HeroSub>
      </Hero>
      <HeroDivider />

      <KpiGrid>
        {kpis.map((k, i) => (
          <Kpi key={k.label} $accent={k.accent.solid} {...fadeUp(i * 0.04)}>
            <KpiLabel>
              <KpiSwatch $tint={k.accent.tint} $color={k.accent.solid}>{k.icon}</KpiSwatch>
              {k.label}
            </KpiLabel>
            <KpiValue>{k.value == null ? '—' : formatNumber(k.value)}</KpiValue>
            <KpiCaption>{k.caption}</KpiCaption>
          </Kpi>
        ))}
      </KpiGrid>

      {(pendingExtensions > 0 || pendingCorrections > 0) && (
        <>
          <SectionHead>
            <SectionTitle>To Dos</SectionTitle>
            <SectionHint>Pending items that need review</SectionHint>
          </SectionHead>
          <TodoStack>
            {pendingExtensions > 0 && (
              <TodoBanner to="/admin/extensions">
                <BannerIcon>
                  <FiBell size={18} />
                </BannerIcon>
                <BannerBody>
                  <BannerTitle>
                    {pendingExtensions} pending extension {pendingExtensions === 1 ? 'request' : 'requests'}
                  </BannerTitle>
                  <BannerCaption>Review and respond on the Extensions page.</BannerCaption>
                </BannerBody>
                <BannerArrow><FiArrowUpRight size={16} /></BannerArrow>
              </TodoBanner>
            )}
            {pendingCorrections > 0 && (
              <TodoBanner to="/admin/attendance-corrections">
                <BannerIcon>
                  <FiCheckCircle size={18} />
                </BannerIcon>
                <BannerBody>
                  <BannerTitle>
                    {pendingCorrections} pending attendance correction {pendingCorrections === 1 ? 'request' : 'requests'}
                  </BannerTitle>
                  <BannerCaption>Review and respond on the Correction Requests page.</BannerCaption>
                </BannerBody>
                <BannerArrow><FiArrowUpRight size={16} /></BannerArrow>
              </TodoBanner>
            )}
          </TodoStack>
        </>
      )}

      <SectionHead>
        <SectionTitle>Quick actions</SectionTitle>
      </SectionHead>
      <QuickRow>
        {quickActions.map((a) => (
          <QuickChip key={a.label} to={a.to}>
            <QuickIcon>{a.icon}</QuickIcon>
            {a.label}
          </QuickChip>
        ))}
      </QuickRow>

      <SectionHead>
        <SectionTitle>Manage</SectionTitle>
        <SectionHint>Six core areas</SectionHint>
      </SectionHead>
      <ManageGrid>
        {manageCards.map((c, i) => (
          <ManageCard
            key={c.to}
            to={c.to}
            $tint={c.accent.tint}
            $color={c.accent.solid}
            {...fadeUp(0.1 + i * 0.04)}
          >
            <ManageHeader>
              <ManageIcon $tint={c.accent.tint} $color={c.accent.solid}>{c.icon}</ManageIcon>
              {c.stat && <ManageStat>{c.stat}</ManageStat>}
            </ManageHeader>
            <ManageTitle>{c.title}</ManageTitle>
            <ManageDesc>{c.desc}</ManageDesc>
            <ManageFooter className="manage-arrow">
              Open
              <FiArrowUpRight size={14} />
            </ManageFooter>
          </ManageCard>
        ))}
      </ManageGrid>

      <SectionHead>
        <SectionTitle>Tools</SectionTitle>
        <SectionHint>Less-frequent operations</SectionHint>
      </SectionHead>
      <ToolList>
        {tools.map((t) => (
          <ToolItem key={t.to} to={t.to}>
            <ToolIcon>{t.icon}</ToolIcon>
            <ToolLabel>
              <ToolName>{t.name}</ToolName>
              <ToolHint>{t.hint}</ToolHint>
            </ToolLabel>
          </ToolItem>
        ))}
      </ToolList>
    </Page>
  );
}

export default AdminDashboard;
